import { useRef, useState } from 'react'
import Styles from './GenerateImageDrawer.module.scss'
import { GenerateImageDrawerProps } from './Types'
import { Drawer } from 'components/Drawer'
import { Form } from './Form'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Button } from 'blaze-ui/Button'
import { HelpDropdown } from 'blaze-components/TopBar/HelpDropdown/HelpDropdown'
import { Close, Help } from 'blaze-ui/icons'
import { GeneratedImage, ImagePrompt } from 'types'
import { useGenerateImages } from './hooks/useGenerateImages'
import { GenerationSets } from './GenerationSets'
import { Heading } from 'blaze-ui/Heading/Heading'
// import { ChangeSomethingMenu } from './ChangeSomethingMenu'

export type PromptValues = Omit<ImagePrompt, 'generation_type' | 'image_style' | 'image_mood'> & { priority: string }

export interface Request {
  id: number
  removedBackgroundUrl?: string
  prompt: PromptValues
  isSaved: boolean
  isFailed: boolean
  generatedContent: null | string
}

export interface GenerationSet {
  uuid: string
  requests: Request[]
  promptDisplay: string
}

export const GenerateImageDrawer = observer((props: GenerateImageDrawerProps) => {
  const { onUse, ...rest } = props
  const { closeDrawer } = useRootStore().drawersStore
  const [showChangeSomethingPrompt] = useState(false)
  const [viewingImage, setViewingImage] = useState<GeneratedImage & { requestId: number }>()
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    handleMultiGenerate,
    generationSets,
    setGenerationSets,
    onRetry,
    previousValues,
    onSaveImage,
    onDeleteImage,
    onGenerateMoreLikeThis,
    processingRequestIds,
  } = useGenerateImages({ containerRef })

  const handleClose = () => {
    if (viewingImage) {
      setViewingImage(undefined)
      return
    }

    closeDrawer()
  }

  const onBack = () => {
    if (viewingImage) {
      setViewingImage(undefined)
      return
    }

    closeDrawer()
  }

  const onConfirm = (img: GeneratedImage) => {
    onUse(img)
    closeDrawer()
  }

  // const openChangeSomethingMenu = () => {
  //   openDrawer(ChangeSomethingMenu, {
  //     onGenerateMore: () => {
  //       if (viewingImage?.requestId) {
  //         onGenerateMoreLikeThis(viewingImage.requestId)
  //         setViewingImage(undefined)
  //         closeDrawer()
  //       }
  //     },
  //     onDescribeAChange: () => {
  //       setShowChangeSomethingPrompt(true)
  //       closeDrawer()
  //     },
  //   })
  // }

  const handleChangeSomething = (values: PromptValues) => {
    // TODO: Send image for modification
  }

  return (
    <Drawer.Root {...rest}>
      <Drawer.Portal>
        <Drawer.Content fullHeight>
          <div style={{ height: '100%' }} data-vaul-no-drag>
            <Drawer.InnerContent color="var(--light-100)" isSnappedTop>
              <Drawer.Top>
                <Drawer.Back onPress={onBack} />
                <Drawer.Title asChild>
                  <Heading level={5}>Generate an Image</Heading>
                </Drawer.Title>
                <div className={Styles.topButtons}>
                  <HelpDropdown trigger={<Button variant="tertiary" size="md" frontIcon={Help} rounded />} />
                  <Button onPress={handleClose} variant="tertiary" size="md" frontIcon={Close} rounded />
                </div>
              </Drawer.Top>
              <Drawer.Container>
                {generationSets.length === 0 && !viewingImage && (
                  <div className={Styles.formWrapper}>
                    <Form onSubmit={(values) => handleMultiGenerate(values)} />
                  </div>
                )}
                {generationSets.length > 0 && !viewingImage && (
                  <div className={Styles.container}>
                    <div className={Styles.generationSets} ref={containerRef}>
                      <GenerationSets
                        generationSets={generationSets}
                        setGenerationSets={setGenerationSets}
                        onConfirm={onConfirm}
                        onSaveImage={onSaveImage}
                        onImagePress={(img: GeneratedImage, requestId: number) =>
                          setViewingImage({ ...img, requestId })
                        }
                        onDeleteImage={onDeleteImage}
                        onGenerateMoreLikeThis={onGenerateMoreLikeThis}
                        onRetry={onRetry}
                        processingRequestIds={processingRequestIds}
                      />
                    </div>
                    <div className={Styles.inputContainer}>
                      <Form
                        showExamples={false}
                        onSubmit={(values) => handleMultiGenerate(values)}
                        initialValues={previousValues}
                      />
                    </div>
                  </div>
                )}
                {viewingImage && (
                  <div className={Styles.viewImageContainer}>
                    <div className={Styles.viewImage}>
                      <img src={viewingImage.url} alt={viewingImage.revisedPrompt} className={Styles.image} />
                    </div>
                    {showChangeSomethingPrompt ? (
                      <div className={Styles.formContainer}>
                        <Form
                          showExamples={false}
                          showChips={false}
                          placeholder="Describe a change"
                          onSubmit={handleChangeSomething}
                        />
                      </div>
                    ) : (
                      <div className={Styles.imageControls}>
                        {/* <Button variant="tertiary" size="lg" fullWidth>
                            Remove Background
                          </Button>
                          <Button variant="tertiary" size="lg" fullWidth onPress={openChangeSomethingMenu}>
                            Change Something
                          </Button> */}
                        <Button size="lg" fullWidth onPress={() => onConfirm(viewingImage)}>
                          Use Image
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Drawer.Container>
            </Drawer.InnerContent>
          </div>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.Root>
  )
})
